import * as React from "react";
import { useEffect } from "react";
import { connect } from "react-redux";
import { actionCreators, getCalendar } from "page/calendar/store";
import {
    actionCreators as burdeApiContentActionCreators,
    getLayoutOptions as getBurdeLayoutOptions,
    getOwnerPageOptions,
} from "burde-api-content/store";
import { actionCreators as uiActionCreators } from "ui/store";
import { actionCreators as eventActionCreators } from "event/store";
import { ApplicationState } from "store";
import { CalendarState, OwnerPageInfo, OwnerPageResponse } from "burde";
import { DefaultCulture, LayoutApiResponse } from "ui/constants";
import {
    getLayoutOptions,
    getOwnerPageImageById,
    getCoverById,
    getBorderById,
    getLayoutById,
    getFonts,
    getFontById,
} from "ui/service";
import { getStmMonthDividerById } from "ui/month-divider-service";
import {
    getCustomImageById,
    getOwnerPageCustomImageInfoFromId,
    getRubberbandByArticleNumber,
    getSpiralByArticleNumber,
} from "calendar/service";
import { fetchDayModuleElements, fetchWeekModuleElements } from "ui/helper";
import { getStmOwnerPageById, getStmOwnerPages } from "UI/owner-page-service";
import { calendarExtraPageCheck, fetchMetadata } from "./helper";

type Props = {
    calendar: CalendarState;
    layoutOptions: LayoutApiResponse | null;
    ownerPageOptions: OwnerPageResponse | null;
} & typeof actionCreators &
    typeof burdeApiContentActionCreators &
    typeof uiActionCreators &
    typeof eventActionCreators;

// This component makes sure that all API content gets fetched throughout the application.
// For example, when a user changes the calendar layoutId to 1203, that layout gets fetched from
// BurdeContentApiService and placed in the burde-api-content/store.
const CalendarContentHandler: React.FC<Props> = ({
    calendar,
    ownerPageOptions,
    updateLayoutId,
    updateSpiralItem,
    updateRubberbandItem,
    updateSelectedLayout,
    updateLayoutOptions,
    updateOwnerPageOptions,
    updateIsLoadingLayoutOptions,
    updateCoverImage,
    updateBackCoverImage,
    updateCustomCoverImage,
    updateCustomBackImage,
    updateMonthDividerInfo,
    updateBorderImage,
    updateSelectedDayModuleElements,
    updateSelectedWeekModuleElements,
    updateSelectedOwnerPage,
    updateOwnerPage,
    updateOwnerPageImageUrl,
    updateExtraPageCollection,
    updateMetadata,
    updateFontOptions,
    updateIsLoadingFontOptions,
    updateSelectedFont,
    updateFontId,
}) => {
    // Set default calendar layout if none is chosen.
    useEffect(() => {
        updateIsLoadingLayoutOptions(true);
        getLayoutOptions(
            calendar.culture || DefaultCulture,
            calendar.calendarSize,
            calendar.calendarType,
        )
            .then((response) => {
                updateLayoutOptions(response);
                if (response !== null) {
                    if (calendar.layoutId === null && response.defaultLayout) {
                        getLayoutById(
                            response.defaultLayout,
                            calendar.culture || DefaultCulture,
                        ).then((response) => {
                            if (response !== null) {
                                updateSelectedLayout(response);
                                updateLayoutId(response.id);
                            }
                        });
                    } else if (calendar.layoutId !== null) {
                        getLayoutById(
                            calendar.layoutId,
                            calendar.culture || DefaultCulture,
                        ).then((response) => {
                            if (response !== null) {
                                updateSelectedLayout(response);
                            }
                        });
                    }
                }
            })
            .finally(() => {
                updateIsLoadingLayoutOptions(false);
            });
    }, [calendar.calendarType]);

    // Layout
    useEffect(() => {
        if (calendar.layoutId !== null) {
            getLayoutById(calendar.layoutId, calendar.culture).then(
                (response) => {
                    updateSelectedLayout(response);
                },
            );
        }
    }, [calendar.layoutId]);

    // Owner page
    useEffect(() => {
        if (!ownerPageOptions) {
            getStmOwnerPages(
                calendar.culture !== null ? calendar.culture : DefaultCulture,
                calendar.calendarSize,
            ).then((response) => {
                if (response) {
                    updateOwnerPageOptions(response);
                    if (calendar.ownerPage.layoutId === 0) {
                        const updatedOwnerPage: OwnerPageInfo = {
                            ...calendar.ownerPage,
                            layoutId: response.defaultLayout,
                        };
                        updateOwnerPage(updatedOwnerPage);
                    }
                }
            });
        }
    }, []);

    useEffect(() => {
        if (
            calendar.ownerPage.layoutId !== null &&
            calendar.ownerPage.layoutId !== 0
        ) {
            getStmOwnerPageById(
                calendar.culture !== null ? calendar.culture : DefaultCulture,
                calendar.calendarSize,
                calendar.ownerPage.layoutId,
            ).then((response) => {
                if (response !== undefined) {
                    updateSelectedOwnerPage(response);
                }
            });
        }
    }, [calendar.ownerPage.layoutId]);

    // Month divider
    useEffect(() => {
        if (calendar.monthDivider && calendar.monthDivider.layoutId !== null) {
            getStmMonthDividerById(
                calendar.culture !== null ? calendar.culture : DefaultCulture,
                calendar.calendarSize,
                calendar.monthDivider.layoutId,
            ).then((response) => {
                if (response !== undefined) {
                    updateMonthDividerInfo(response);
                }
            });
        } else if (calendar.monthDivider === null) {
            updateMonthDividerInfo(null);
        }
    }, [calendar.monthDivider]);

    // Spiral
    useEffect(() => {
        if (calendar.spiral !== "") {
            getSpiralByArticleNumber(calendar.spiral).then((response) => {
                updateSpiralItem(response);
            });
        }
    }, [calendar.spiral]);

    // Rubberband
    useEffect(() => {
        if (calendar.rubberband !== "") {
            getRubberbandByArticleNumber(calendar.rubberband).then(
                (response) => {
                    updateRubberbandItem(response);
                },
            );
        }
    }, [calendar.rubberband]);

    // Custom cover image
    useEffect(() => {
        if (calendar.customCoverImageId !== null) {
            getCustomImageById(calendar.customCoverImageId).then((response) => {
                updateCustomCoverImage(response);
            });
        } else {
            updateCustomCoverImage(null);
        }
    }, [calendar.customCoverImageId]);

    // Custom back cover image
    useEffect(() => {
        if (calendar.customBackImageId !== null) {
            getCustomImageById(calendar.customBackImageId).then((response) => {
                updateCustomBackImage(response);
            });
        } else {
            updateCustomBackImage(null);
        }
    }, [calendar.customBackImageId]);

    // Cover image
    useEffect(() => {
        if (calendar.coverImageId !== null) {
            const culture =
                calendar.culture !== null ? calendar.culture : DefaultCulture;

            getCoverById(calendar.coverImageId, culture).then((response) => {
                updateCoverImage(response);
            });
        } else {
            updateCoverImage(null);
        }
    }, [calendar.coverImageId]);

    // Back cover image
    useEffect(() => {
        if (calendar.backCoverImageId !== null) {
            const culture =
                calendar.culture !== null ? calendar.culture : DefaultCulture;

            getCoverById(calendar.backCoverImageId, culture).then(
                (response) => {
                    updateBackCoverImage(response);
                },
            );
        } else {
            updateBackCoverImage(null);
        }
    }, [calendar.backCoverImageId]);

    // Border image
    useEffect(() => {
        if (calendar.borderImageId !== null) {
            const culture =
                calendar.culture !== null ? calendar.culture : DefaultCulture;

            getBorderById(calendar.borderImageId, culture).then((response) => {
                updateBorderImage(response);
            });
        } else {
            updateBorderImage(null);
        }
    }, [calendar.borderImageId]);

    // Day modules
    useEffect(() => {
        fetchDayModuleElements(calendar).then((response) => {
            updateSelectedDayModuleElements(response);
        });
    }, [calendar.dayModules]);

    // Week modules
    useEffect(() => {
        fetchWeekModuleElements(calendar).then((response) => {
            updateSelectedWeekModuleElements(response);
        });
    }, [calendar.weekModules]);

    // Owner page
    useEffect(() => {
        let isMounted = true;
        if (calendar.ownerPage.customImageId !== null) {
            getOwnerPageCustomImageInfoFromId(
                calendar.ownerPage.customImageId,
            ).then((image) => {
                if (image && image.url && isMounted) {
                    updateOwnerPageImageUrl(image.url);
                }
            });
        } else if (calendar.ownerPage.imageId !== null) {
            getOwnerPageImageById(
                calendar.ownerPage.imageId,
                calendar.culture !== null ? calendar.culture : DefaultCulture,
            ).then((image) => {
                if (image && image.endpoints && isMounted) {
                    updateOwnerPageImageUrl(image.endpoints.preview);
                }
            });
        } else {
            updateOwnerPageImageUrl(null);
        }
        return () => {
            isMounted = false;
        };
    }, [calendar.ownerPage.imageId, calendar.ownerPage.customImageId]);

    // Overflow check for extra pages
    useEffect(() => {
        calendarExtraPageCheck(
            calendar.extraPages,
            calendar.calendarType,
            calendar.calendarSize,
            calendar.customNumberOfEndPages,
            updateExtraPageCollection,
        );
    }, [
        calendar.extraPages,
        calendar.customNumberOfEndPages,
        calendar.calendarType,
    ]);

    // Metadata
    useEffect(() => {
        const culture =
            calendar.culture !== null ? calendar.culture : DefaultCulture;
        fetchMetadata(calendar.startDate, culture).then((response) => {
            updateMetadata(response);
        });
    }, [calendar.startDate]);

    // Font options
    useEffect(() => {
        updateIsLoadingFontOptions(true);
        getFonts(
            calendar.culture || DefaultCulture,
            calendar.calendarSize,
            calendar.calendarType,
        )
            .then((fontResponse) => {
                updateFontOptions(fontResponse);
                if (fontResponse !== null) {
                    if (calendar.fontId === null && fontResponse.default) {
                        getFontById(
                            fontResponse.default,
                            calendar.culture || DefaultCulture,
                        ).then((response) => {
                            if (response !== null) {
                                updateSelectedFont(response);
                                updateFontId(response.id);
                            }
                        });
                    } else if (calendar.fontId !== null) {
                        getFontById(
                            calendar.fontId,
                            calendar.culture || DefaultCulture,
                        ).then((response) => {
                            if (response !== null) {
                                updateSelectedFont(response);
                            } else {
                                updateFontId(fontResponse.default);
                            }
                        });
                    }
                }
            })
            .finally(() => {
                updateIsLoadingFontOptions(false);
            });
    }, [calendar.fontId]);

    return <div></div>;
};

const mapStateToProps = (state: ApplicationState) => ({
    calendar: getCalendar(state),
    layoutOptions: getBurdeLayoutOptions(state),
    ownerPageOptions: getOwnerPageOptions(state),
});

const mapDispatchToProps = {
    ...actionCreators,
    ...burdeApiContentActionCreators,
    ...eventActionCreators,
    ...uiActionCreators,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(CalendarContentHandler);
